import { connected, disconnected, messageReceived, errorOccurred } from '../slices/socketSlice';
import { performGlobalLogout } from '../slices/authSlice';

let activeSocket = null;
let reconnectTimer = null;
let reconnectEndpoint = null;

const clearReconnectTimer = () => {
	if (reconnectTimer) {
		clearTimeout(reconnectTimer);
		reconnectTimer = null;
	}
};

const scheduleReconnect = (store, endpoint) => {
	if (!endpoint) return;
	clearReconnectTimer();
	reconnectTimer = setTimeout(() => {
		reconnectTimer = null;
		if (!activeSocket && endpoint) {
			store.dispatch({ type: 'socket/connect', payload: endpoint });
		}
	}, 1500);
};

// Detach all handlers from a socket so it can no longer affect Redux state,
// then close it. Used whenever we're replacing or tearing down a connection.
const detachAndClose = (socket) => {
	if (!socket) return;
	socket.onopen = null;
	socket.onmessage = null;
	socket.onerror = null;
	socket.onclose = null;
	try {
		socket.close();
	} catch (err) {
		// socket may already be closing/closed - safe to ignore
	}
};

export const websocketMiddleware = (store) => (next) => (action) => {
	if (action.type === 'socket/connect') {
		try {
			clearReconnectTimer();
			reconnectEndpoint = action.payload;

			// Tear down any existing connection first so its (possibly delayed)
			// events can never fire after the new socket takes over.
			if (activeSocket) {
				detachAndClose(activeSocket);
				activeSocket = null;
			}

			const endpoint = action.payload;
			const socket = new WebSocket(endpoint);
			activeSocket = socket;

			// Every handler below checks `activeSocket === socket` before dispatching.
			// This guards against a stale/late event from a socket that has since
			// been replaced (e.g. User A's socket closing after User B has already
			// connected) from ever touching the store.
			socket.onopen = () => {
				if (activeSocket === socket) {
					store.dispatch(connected());
				}
			};

			socket.onmessage = (event) => {
				if (activeSocket !== socket) return;
				try {
					const data = JSON.parse(event.data);
					store.dispatch(messageReceived(data));
				} catch (err) {
					store.dispatch(errorOccurred('Invalid JSON in socket message'));
				}
			};

			socket.onerror = () => {
				if (activeSocket === socket) {
					store.dispatch(errorOccurred('WebSocket error'));
				}
			};

			socket.onclose = () => {
				if (activeSocket === socket) {
					activeSocket = null;
					store.dispatch(disconnected());
					if (reconnectEndpoint) {
						scheduleReconnect(store, reconnectEndpoint);
					}
				}
			};
		} catch (err) {
			store.dispatch(errorOccurred('WebSocket init failed'));
		}
	}

	if (action.type === 'socket/disconnect') {
		clearReconnectTimer();
		reconnectEndpoint = null;
		if (activeSocket) {
			detachAndClose(activeSocket);
			activeSocket = null;
		}
		// Dispatch synchronously rather than waiting for the async onclose event,
		// so state updates immediately and can't race with a new connection.
		store.dispatch(disconnected());
	}

	if (action.type === 'socket/send' && activeSocket && activeSocket.readyState === WebSocket.OPEN) {
		const message = typeof action.payload === 'string' ? action.payload : JSON.stringify(action.payload);
		activeSocket.send(message);
	}

	// Handle global logout - disconnect WebSocket
	if (action.type === performGlobalLogout.fulfilled.type) {
		clearReconnectTimer();
		reconnectEndpoint = null;
		if (activeSocket) {
			detachAndClose(activeSocket);
			activeSocket = null;
		}
		// Dispatch synchronously so socketSlice state is cleared immediately on
		// logout, instead of depending on the closing socket's async onclose -
		// which could otherwise fire after the next user's socket has connected.
		store.dispatch(disconnected());
	}

	return next(action);
};